/**
 * ArchLang v0.10 — table representation data model (S13b, engine layer).
 *
 * `computeTableData(view, plan, ctx)` turns a `view` carrying a `table { … }`
 * clause into a **`TableData`**: a column header list + one row per shown
 * element, every cell resolved to a serialization-clean scalar. It is pure over
 * the landed expression evaluator (`evalExpression`) — no scene types, no
 * rendering. The board layer (`ViewTable`, S15) is a thin renderer over it.
 *
 * ## Rows = the shown selection
 * When a `ViewProjectionPlan` is given, rows are its `shown` keys (the view's
 * `show`/`hide` selection). Without a plan (a table-only view has no projecting
 * clause, so `planView` returns `null`), rows fall back to the whole node
 * universe. Each row's subject is the `ElementNode` for its qualified key.
 *
 * ## Editability (the write-path contract, S13a)
 * A **getter-form** column (`column @g ["Title"]`) is editable IFF it targets a
 * writable model slot: an `@@aspect` write target, or an ordinary `@field`
 * getter. Builtins (`@name`, `@connections`, `@todos`, …) and capability getters
 * (`@ver.lastChanged`, `@ver.changes(…)`) are derived — read-only. A
 * **calc-form** column (`column ["T"]: <expr>`) is always read-only. Editable
 * columns carry `writeSigil` + `writePath` so `setFieldCellEdit` can target the
 * origin element (resolved via the row's `#id` / qualified key).
 *
 * ## Determinism
 * `computeTableData` is a pure function of `(view, plan, model, graph)`. Rows are
 * ordered by `sort by <getter>` (stable, key as tie-breaker) when present, else
 * lexicographically by qualified key. No `Math.random` / `Date`.
 */

import type { StableId } from "./types.js";
import type { Expr, ExprGetter } from "./selector-ast.js";
import { BUILTIN_GETTERS } from "./selector-ast.js";
import type { TableClause, TableColumn, ViewDeclV2 } from "./v010-ast.js";
import type { ElementNode, EvalContext } from "./selector-eval.js";
import { evalExpression, UnsupportedExprError, type ExprValue } from "./expression-eval.js";
import { coreCapabilityRegistry } from "./capability-registry.js";
import type { ViewProjectionPlan } from "./view-projection.js";

// =============================================================================
// Shape
// =============================================================================

/** A resolved cell value — JSON-clean scalars; `null` = absent / deferred. */
export type TableCell = string | number | boolean | null;

/** One column header + its render/write metadata. */
export interface TableColumnData {
  /** Display title — the column's string, else a humanized getter name. */
  readonly title: string;
  /** True = an editable model slot (a getter targeting a field/aspect). */
  readonly editable: boolean;
  /** The column's syntactic form. */
  readonly kind: "getter" | "calc";
  /** Editable columns only — the getter's sigil (`setFieldCellEdit` write target). */
  readonly writeSigil?: "@" | "@@";
  /** Editable columns only — the dotted field/aspect path to write. */
  readonly writePath?: readonly string[];
}

/** One table row — its origin identity + the resolved cells (column-aligned). */
export interface TableRowData {
  /** The row subject's qualified key (the origin the write path targets). */
  readonly key: string;
  /** The subject's `#id`, when it carries one — preferred over `key` for writes. */
  readonly id?: StableId;
  readonly cells: readonly TableCell[];
}

/** The full table projection — column headers + rows, both deterministic. */
export interface TableData {
  readonly columns: readonly TableColumnData[];
  readonly rows: readonly TableRowData[];
}

// =============================================================================
// computeTableData
// =============================================================================

/**
 * Project a `view` with a `table` clause into a {@link TableData}. Returns `null`
 * for a view that carries no `TableClause` (nothing to tabulate).
 */
export function computeTableData(
  view: ViewDeclV2,
  plan: ViewProjectionPlan | null,
  ctx: EvalContext,
): TableData | null {
  const table = view.clauses.find((c): c is TableClause => c.kind === "table");
  if (!table) return null;

  const columns = table.columns.map((col) => columnDataOf(col, ctx));

  // Rows = the shown selection (plan) or the whole universe (table-only view).
  const rowKeys = plan
    ? [...plan.shown]
    : ctx.universe.nodes.map((n) => n.key);

  const idByKey = idIndex(plan, ctx);

  const rows: { row: TableRowData; sortValue: TableCell }[] = [];
  for (const key of rowKeys) {
    const subject = ctx.universe.byKey(key);
    if (!subject) continue; // shown key with no node (defensive) — skip
    const cells = table.columns.map((col) => cellValue(col, subject, ctx));
    const sortValue = table.sortBy ? evalCell(table.sortBy, subject, ctx) : null;
    rows.push({ row: { key, id: idByKey.get(key), cells }, sortValue });
  }

  rows.sort((a, b) => {
    if (table.sortBy) {
      const c = compareCells(a.sortValue, b.sortValue);
      if (c !== 0) return c;
    }
    return a.row.key.localeCompare(b.row.key);
  });

  return { columns, rows: rows.map((r) => r.row) };
}

// =============================================================================
// Columns
// =============================================================================

function columnDataOf(col: TableColumn, ctx: EvalContext): TableColumnData {
  if (col.kind === "calc") {
    return { title: col.title, editable: false, kind: "calc" };
  }
  const editable = isEditableGetter(col.getter, ctx);
  return {
    title: col.title ?? humanizeGetter(col.getter),
    editable,
    kind: "getter",
    ...(editable ? { writeSigil: col.getter.sigil, writePath: col.getter.path } : {}),
  };
}

/**
 * A getter column is editable IFF it names a writable model slot: an `@@aspect`
 * write target, or an ordinary `@field` getter. Builtins (`@name`/`@connections`
 * /…) and capability getters (`@ver.…`, or any `@ns.g(args)`) are derived values
 * — read-only.
 */
function isEditableGetter(g: ExprGetter, ctx: EvalContext): boolean {
  if (g.sigil === "@@") return g.capabilityArgs === undefined; // aspect write target
  if (g.sigil !== "@") return false;
  if (g.capabilityArgs !== undefined) return false; // `@ns.g(args)` — a capability
  // A builtin (or a dotted builtin's subtree, e.g. `@estimatedLatency.min`) is
  // derived — never an editable cell. The reservation covers the whole subtree.
  if (BUILTIN_GETTERS.has(g.path[0]!)) return false;
  // A dotted `@` getter over a REGISTERED capability namespace (`@ver.lastChanged`)
  // is derived; a dotted field (`@repo.url`) is a plain, editable field.
  if (g.path.length >= 2) {
    const top = g.path[0]!;
    const registry = ctx.capabilities?.registry ?? coreCapabilityRegistry();
    if (registry.hasNamespace(top)) return false;
  }
  return true;
}

/** The last path segment, humanized (`maxConn` → `Max Conn`, `team` → `Team`). */
function humanizeGetter(g: ExprGetter): string {
  const leaf = g.path[g.path.length - 1] ?? "";
  return leaf
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// =============================================================================
// Cells
// =============================================================================

function cellValue(col: TableColumn, subject: ElementNode, ctx: EvalContext): TableCell {
  const expr: Expr = col.kind === "getter" ? col.getter : col.expr;
  return evalCell(expr, subject, ctx);
}

/** Evaluate an expression to a cell scalar; unresolved / deferred → `null`. */
function evalCell(expr: Expr, subject: ElementNode, ctx: EvalContext): TableCell {
  let v: ExprValue;
  try {
    v = evalExpression(expr, subject, ctx);
  } catch (e) {
    // Deferred builtins (`@todos`, …) / list-valued aspect getters throw here —
    // a report cell has no value to show, so it reads as absent.
    if (e instanceof UnsupportedExprError) return null;
    throw e;
  }
  return v === undefined ? null : v;
}

/** Deterministic cell ordering: nulls last, numbers numerically, else by text. */
function compareCells(a: TableCell, b: TableCell): number {
  const an = a === null;
  const bn = b === null;
  if (an || bn) return an === bn ? 0 : an ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

// =============================================================================
// Stable-id index
// =============================================================================

/** Qualified key → `#id`, from the plan (when present) or the derived graph. */
function idIndex(plan: ViewProjectionPlan | null, ctx: EvalContext): ReadonlyMap<string, StableId> {
  if (plan) return plan.stableIds;
  const out = new Map<string, StableId>();
  if (ctx.graph) {
    for (const [m, path] of ctx.graph.pathOfModule) {
      if (m.id) out.set(path.join("."), m.id);
    }
  }
  return out;
}
